import {
  AlertTriangleIcon,
  CalendarIcon,
  CheckCircle2Icon,
  LoaderIcon,
} from 'lucide-react'

import { Badge } from 'slab'

import { type CombinedInvoice, formatInvoiceDate, isStripeInvoice } from '../../utils/billing'
import {
  type EmployersEmployerInvoice,
  type EmployersStripeInvoice,
} from '../../utils/web_api.schemas'

type InvoiceForBadge =
  | EmployersEmployerInvoice
  | EmployersStripeInvoice

interface InvoiceStatusBadgeProps {
  invoice: InvoiceForBadge
  scheduledPaymentDate?: string | null
  paymentStatus?: 'paid' | 'scheduled' | 'processing' | 'failed'
  paymentError?: string | null
}

interface NormalizedInvoiceStatus {
  paidAt: string | null
  scheduledDate: string | null
  isProcessing: boolean
  isFailed: boolean
  errorMessage: string | null
}

function normalizeStripeInvoiceStatus (
  invoice: EmployersStripeInvoice & { type: string }
): NormalizedInvoiceStatus {
  const paidAt = invoice.paid_at || null

  let scheduledDate: string | null = null
  if (invoice.due_date) {
    const dueDate = new Date(invoice.due_date)
    const now = new Date()
    if (dueDate > now) {
      scheduledDate = invoice.due_date
    }
  }

  const isProcessing = invoice.status === 'open'
  const isFailed = invoice.status === 'uncollectible' || invoice.status === 'void'
  const errorMessage = isFailed ? 'Payment failed' : null

  return {
    paidAt,
    scheduledDate,
    isProcessing,
    isFailed,
    errorMessage,
  }
}

function normalizeEmployerInvoiceStatus (
  invoice: EmployersEmployerInvoice,
  scheduledPaymentDate?: string | null,
  paymentStatus?: string,
  paymentError?: string | null
): NormalizedInvoiceStatus {
  const paidAt = invoice.paid_at || null
  const scheduledDate = scheduledPaymentDate || null
  const isProcessing = paymentStatus === 'processing'
  const isFailed = paymentStatus === 'failed'
  const errorMessage = isFailed ? (paymentError || 'Payment failed') : null

  return {
    paidAt,
    scheduledDate,
    isProcessing,
    isFailed,
    errorMessage,
  }
}

function normalizeInvoiceStatus (
  invoice: InvoiceForBadge,
  scheduledPaymentDate?: string | null,
  paymentStatus?: string,
  paymentError?: string | null
): NormalizedInvoiceStatus {
  const invoiceWithType = invoice as CombinedInvoice
  if (isStripeInvoice(invoiceWithType)) {
    return normalizeStripeInvoiceStatus(invoiceWithType)
  }

  return normalizeEmployerInvoiceStatus(
    invoice as EmployersEmployerInvoice,
    scheduledPaymentDate,
    paymentStatus,
    paymentError
  )
}

export function InvoiceStatusBadge ({
  invoice,
  scheduledPaymentDate,
  paymentStatus,
  paymentError,
}: InvoiceStatusBadgeProps) {
  const status = normalizeInvoiceStatus(invoice, scheduledPaymentDate, paymentStatus, paymentError)

  if (status.paidAt) {
    return (
      <Badge type='success' leftIcon={CheckCircle2Icon}>
        Paid {formatInvoiceDate(status.paidAt)}
      </Badge>
    )
  }

  if (status.scheduledDate) {
    return (
      <Badge type='info' leftIcon={CalendarIcon}>
        Scheduled {formatInvoiceDate(status.scheduledDate)}
      </Badge>
    )
  }

  if (status.isProcessing) {
    return (
      <Badge type='neutral' leftIcon={LoaderIcon}>
        Payment processing
      </Badge>
    )
  }

  if (status.isFailed) {
    return (
      <Badge type='critical' leftIcon={AlertTriangleIcon}>
        Failed: {status.errorMessage}
      </Badge>
    )
  }

  return (
    <Badge type='neutral'>
      Pending
    </Badge>
  )
}
